import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Save, Search, ChevronDown, ChevronUp, GraduationCap, School,
  Users, Loader2, AlertTriangle, Trophy, ArrowUpDown, RefreshCw, FileText
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";


const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yid) return Number(yid);
  } catch (e) { console.warn('getStoredYearId error:', e); }
  return null;
};
const t = {
  fr: {
    title: "Conseil de classe express",
    subtitle: "Vérification et modification individuelle des résultats du conseil de classe",
    class: "Classe",
    period: "Période / Trimestre",
    showNotes: "Afficher les notes",
    sortByMerit: "Trier par mérite",
    sortByAlpha: "Trier alphabétique",
    no: "N°",
    student: "Élève",
    work: "Travail",
    conduct: "Conduite",
    decision: "Décision",
    subjects: "Matières",
    trim1: "TR 1", trim2: "TR 2", trim3: "TR 3", annual: "An",
    coefs: "Coefs composés", coefsValidated: "Coefs validés", coefsFailed: "Coefs échoués",
    isRanked: "Élève classé", yes: "OUI", isNotRanked: "NON",
    annualAvg: "Moyenne Annuelle", annualRank: "Rang Annuel",
    workDecisions: "TRAVAIL > Décisions",
    doNotClassify: "Ne pas classer cet élève",
    honorRoll: "Tableau d'honneur", encouragements: "Encouragements", felicitations: "Félicitations",
    avertTravail: "Avertissement Travail", blameTravail: "Blâme Travail",
    obsProf: "Observation du prof principal",
    absJustified: "Absences justifiées", absUnjustified: "Absences non justifiées", absTotal: "Absences totales",
    exclusionDays: "Jours d'exclusion", exclusionHours: "Heures d'exclusion",
    consigneDays: "Jours de consigne", consigneHours: "Heures de consigne",
    conductNote: "Note discipline / 20",
    avertConduite: "Avertissement Conduite", blameConduite: "Blâme Conduite",
    obsSurveillant: "Observation du surveillant général",
    finalDecision: "Décision",
    repeat: "Redouble", admitted: "Admis", retrograde: "Rétrogradé", excluded: "Exclu", none: "Aucune",
    obsChef: "Observation du chef de l'établissement",
    obsConseil: "Observation du conseil de classe",
    save: "Sauvegarder", saving: "Sauvegarde...",
    success: "Succès", error: "Erreur", loading: "Chargement...", noData: "Aucune donnée",
    print: "Imprimer", export: "Exporter", close: "Fermer",
    search: "Rechercher élève...",
  },
  en: {
    title: "Express Class Council",
    subtitle: "Individual verification and modification of class council results",
    class: "Class", period: "Period / Term", showNotes: "Show grades",
    sortByMerit: "Sort by merit", sortByAlpha: "Sort alphabetically",
    no: "No.", student: "Student", work: "Work", conduct: "Conduct", decision: "Decision",
    subjects: "Subjects", trim1: "T1", trim2: "T2", trim3: "T3", annual: "An",
    coefs: "Total coefs", coefsValidated: "Validated coefs", coefsFailed: "Failed coefs",
    isRanked: "Student ranked", yes: "YES", isNotRanked: "NO",
    annualAvg: "Annual Average", annualRank: "Annual Rank",
    workDecisions: "WORK > Decisions",
    doNotClassify: "Do not classify this student",
    honorRoll: "Honor roll", encouragements: "Encouragements", felicitations: "Congratulations",
    avertTravail: "Work Warning", blameTravail: "Work Blame",
    obsProf: "Class teacher observation",
    absJustified: "Justified absences", absUnjustified: "Unjustified absences", absTotal: "Total absences",
    exclusionDays: "Exclusion days", exclusionHours: "Exclusion hours",
    consigneDays: "Detention days", consigneHours: "Detention hours",
    conductNote: "Conduct note / 20",
    avertConduite: "Conduct Warning", blameConduite: "Conduct Blame",
    obsSurveillant: "Supervisor observation",
    finalDecision: "Decision",
    repeat: "Repeat", admitted: "Admitted", retrograde: "Demoted", excluded: "Excluded", none: "None",
    obsChef: "Principal observation",
    obsConseil: "Class council observation",
    save: "Save", saving: "Saving...",
    success: "Success", error: "Error", loading: "Loading...", noData: "No data",
    print: "Print", export: "Export", close: "Close",
    search: "Search student...",
  }
};

interface SubjectData {
  subject_id: number; subject_name: string; subject_code: string; coefficient: number;
  trimester_scores: Record<string, number>; annual_average: number;
}

interface StudentCouncil {
  student_id: number; matricule: string; first_name: string; last_name: string;
  full_name: string; gender: string; age: number; is_repeating: boolean; photo_url: string | null;
  subjects: SubjectData[]; general_average: number; annual_rank: number; total_students: number;
  absence_hours: number; absence_unjustified: number; detention_days: number;
  exclusion_days: number; exclusion_hours: number; consigne_days: number; consigne_hours: number;
  conduct_note: number; current_decision: string | null; current_next_class_id: number | null;
  current_next_class_name: string | null; tableau_honneur: boolean; encouragements: boolean;
  felicitations: boolean; avertissement_travail: boolean; blame_travail: boolean;
  avertissement_conduite: boolean; blame_conduite: boolean;
  obs_prof_principal: string | null; obs_surveillant: string | null;
  obs_chef_etablissement: string | null; obs_conseil: string | null;
}

export default function ClassCouncilExpressPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const { classId: urlClassId } = useParams<{ classId: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState(urlClassId || "");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const storedYearId = getStoredYearId();
  const [students, setStudents] = useState<StudentCouncil[]>([]);
  const [expandedStudent, setExpandedStudent] = useState<number | null>(null);
  const [showNotes, setShowNotes] = useState(true);
  const [sortBy, setSortBy] = useState<"merit" | "alpha">("merit");
  const [searchQuery, setSearchQuery] = useState("");

  const schoolId = user?.school_id || 1;

  // ─── CHARGEMENT INITIAL ───
  useEffect(() => {
    if (!schoolId) { console.warn("[Express] schoolId undefined"); return; }
    console.log("[Express] Mounting with schoolId:", schoolId);

    // Charger les classes
    apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } })
      .then((res: any) => {
        console.log("[Express] /classes raw:", res);
        let list: any[] = [];
        if (Array.isArray(res)) list = res;
        else if (Array.isArray(res?.items)) list = res.items;
        else if (Array.isArray(res?.data)) list = res.data;
        console.log("[Express] classes parsed:", list.length, list);
        setClasses(list);
        // Si un classId est passé dans l'URL, le valider
        if (urlClassId && list.length > 0) {
          const found = list.find((c: any) => String(c.id) === urlClassId);
          if (found) {
            console.log("[Express] URL classId found in list:", urlClassId);
            setSelectedClass(urlClassId);
          } else {
            console.warn("[Express] URL classId not found in loaded classes:", urlClassId);
          }
        }
        if (list.length === 0) toast.info(lang === 'fr' ? 'Aucune classe trouvée' : 'No classes found');
      })
      .catch((e: any) => {
        console.error("[Express] /classes error:", e?.response?.status, e?.response?.data);
        toast.error(`${lang === 'fr' ? 'Erreur classes' : 'Classes error'}: ${e?.response?.data?.detail || e?.message}`);
      });

    // Charger les périodes (essayer plusieurs endpoints)
    const tryEndpoints = ["/grades/periods", "/evaluation-periods", "/grades/sequences", "/sequences"];
    let found = false;
    const tryNext = (idx: number) => {
      if (idx >= tryEndpoints.length || found) return;
      apiService.get(tryEndpoints[idx], { params: { school_id: schoolId } })
        .then((res: any) => {
          console.log(`[Express] ${tryEndpoints[idx]} raw:`, res);
          let list: any[] = [];
          if (Array.isArray(res)) list = res;
          else if (Array.isArray(res?.items)) list = res.items;
          else if (Array.isArray(res?.data)) list = res.data;
          console.log(`[Express] periods from ${tryEndpoints[idx]}:`, list.length, list);
          if (list.length > 0) { setPeriods(list); found = true; }
          else tryNext(idx + 1);
        })
        .catch(() => tryNext(idx + 1));
    };
    tryNext(0);
  }, [schoolId, lang]);

  // ─── FORCER LE CHARGEMENT SI URL classId EST PRÉSENT ───
  useEffect(() => {
    if (urlClassId && selectedClass === urlClassId && classes.length > 0) {
      console.log("[Express] Forcing student load from URL classId:", urlClassId);
      // Le useEffect principal de selectedClass va se déclencher
    }
  }, [urlClassId, classes]);

  // ─── CHARGEMENT DES ÉLÈVES ───
  useEffect(() => {
    if (!selectedClass) { setStudents([]); return; }
    setLoading(true);
    const params: any = { academic_year_id: storedYearId || 1 };
    if (selectedPeriod) params.evaluation_period_id = selectedPeriod;

    apiService.get(`/class-council/express/${selectedClass}`, { params })
      .then((res: any) => {
        console.log("[Express] students raw:", res);
        const data = res?.data || res;
        const list = data?.students || (Array.isArray(data) ? data : []);
        console.log("[Express] students parsed:", list.length);
        if (sortBy === "merit") list.sort((a: any, b: any) => (b.general_average || 0) - (a.general_average || 0));
        else list.sort((a: any, b: any) => (a.full_name || "").localeCompare(b.full_name || ""));
        setStudents(list);
      })
      .catch((e: any) => {
        console.error("[Express] students error:", e?.response?.data);
        toast.error(e?.response?.data?.detail || txt.error);
      })
      .finally(() => setLoading(false));
  }, [selectedClass, selectedPeriod, sortBy, txt.error]);

  const filteredStudents = students.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (s.full_name || "").toLowerCase().includes(q) || (s.matricule || "").toLowerCase().includes(q);
  });

  const updateStudentField = (studentId: number, field: keyof StudentCouncil, value: any) => {
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, [field]: value } : s));
  };

  const saveStudent = async (student: StudentCouncil) => {
    setSaving(true);
    try {
      await apiService.put(`/class-council/express/${selectedClass}/student/${student.student_id}`, {
        general_average: student.general_average, annual_rank: student.annual_rank,
        tableau_honneur: student.tableau_honneur, encouragements: student.encouragements,
        felicitations: student.felicitations, avertissement_travail: student.avertissement_travail,
        blame_travail: student.blame_travail, absence_hours: student.absence_hours,
        absence_unjustified: student.absence_unjustified, detention_days: student.detention_days,
        exclusion_days: student.exclusion_days, exclusion_hours: student.exclusion_hours,
        consigne_days: student.consigne_days, consigne_hours: student.consigne_hours,
        conduct_note: student.conduct_note, avertissement_conduite: student.avertissement_conduite,
        blame_conduite: student.blame_conduite, decision: student.current_decision,
        next_class_id: student.current_next_class_id, is_repeating: student.is_repeating,
        obs_prof_principal: student.obs_prof_principal, obs_surveillant: student.obs_surveillant,
        obs_chef_etablissement: student.obs_chef_etablissement, obs_conseil: student.obs_conseil,
      });
      toast.success(txt.success);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    } finally { setSaving(false); }
  };

  const getAppreciationColor = (score: number) => {
    if (score >= 16) return "text-emerald-700";
    if (score >= 14) return "text-blue-700";
    if (score >= 12) return "text-cyan-700";
    if (score >= 10) return "text-gray-700";
    if (score >= 8) return "text-orange-700";
    return "text-red-700";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-[#1e3a8a]" /> {txt.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{txt.subtitle}</p>
      </div>

      {/* DEBUG: afficher le nombre de classes et périodes chargées */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-800">
        <strong>Debug:</strong> schoolId={schoolId} | urlClassId={urlClassId || "none"} | classes={classes.length} | periods={periods.length} | selectedClass={selectedClass || "none"} | yearId={storedYearId || "default"}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase">{txt.class} *</label>
          {classes.length > 0 ? (
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[200px] focus:ring-2 focus:ring-[#1e3a8a]">
              <option value="">{lang === 'fr' ? 'Sélectionner une classe...' : 'Select a class...'}</option>
              {classes.map(c => (
                <option key={c.id} value={String(c.id)}>{c.name || c.code || `Classe ${c.id}`}</option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-2">
              <input type="number" value={selectedClass} onChange={e => setSelectedClass(e.target.value)} placeholder="ID classe"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:ring-2 focus:ring-[#1e3a8a]" />
              <span className="text-xs text-amber-600">{lang === 'fr' ? 'Classes non chargées' : 'Classes not loaded'}</span>
            </div>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase">{txt.period}</label>
          {periods.length > 0 ? (
            <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[200px] focus:ring-2 focus:ring-[#1e3a8a]">
              <option value="">{lang === 'fr' ? 'Annuelle' : 'Annual'}</option>
              {periods.map(p => (
                <option key={p.id} value={String(p.id)}>{p.label_fr || p.label_en || p.code || `Période ${p.id}`}</option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-2">
              <input type="number" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} placeholder="ID période"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:ring-2 focus:ring-[#1e3a8a]" />
              <span className="text-xs text-amber-600">{lang === 'fr' ? 'Périodes non chargées' : 'Periods not loaded'}</span>
            </div>
          )}
        </div>
        <div className="h-8 w-px bg-gray-200" />
        <button onClick={() => setShowNotes(!showNotes)}
          className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${showNotes ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-700'}`}>
          <FileText className="w-4 h-4" /> {txt.showNotes}
        </button>
        <button onClick={() => setSortBy(sortBy === "merit" ? "alpha" : "merit")}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4" /> {sortBy === "merit" ? txt.sortByAlpha : txt.sortByMerit}
        </button>
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder={txt.search} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-48" />
        </div>
      </div>

      {/* Students List */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
          <p className="text-sm text-gray-500 mt-2">{txt.loading}</p>
        </div>
      ) : !selectedClass ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          <School className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{lang === 'fr' ? 'Sélectionnez une classe' : 'Select a class'}</p>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{txt.noData}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredStudents.map((student, idx) => (
            <div key={student.student_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedStudent(expandedStudent === student.student_id ? null : student.student_id)}>
                <span className="text-sm font-mono text-gray-400 w-8">{idx + 1}</span>
                <div className="w-10 h-10 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center text-xs font-bold text-[#1e3a8a] flex-shrink-0">
                  {(student.first_name?.[0] || '') + (student.last_name?.[0] || '')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                  <div className="text-xs text-gray-500">{student.matricule} — {student.gender === 'F' ? 'F' : 'M'} — {student.age} ans</div>
                </div>
                <div className="text-center px-3">
                  <div className={`text-lg font-bold ${getAppreciationColor(student.general_average)}`}>
                    {student.general_average.toFixed(2)} / 20
                  </div>
                  <div className="text-[10px] text-gray-500">{txt.annualAvg}</div>
                </div>
                <div className="text-center px-3">
                  <div className="text-lg font-bold text-[#1e3a8a]">{student.annual_rank}</div>
                  <div className="text-[10px] text-gray-500">/ {student.total_students}</div>
                </div>
                <div className="text-center px-3">
                  <div className="text-lg font-bold text-gray-700">{student.conduct_note}</div>
                  <div className="text-[10px] text-gray-500">{txt.conductNote}</div>
                </div>
                <div className="px-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    student.current_decision?.includes('Admi') ? 'bg-emerald-100 text-emerald-700' :
                    student.current_decision?.includes('Redoubl') ? 'bg-amber-100 text-amber-700' :
                    student.current_decision?.includes('Exclu') ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{student.current_decision || txt.none}</span>
                </div>
                <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
                  {expandedStudent === student.student_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>

              {expandedStudent === student.student_id && (
                <div className="border-t border-gray-200 px-4 py-4 bg-gray-50/50">
                  {showNotes && student.subjects.length > 0 && (
                    <div className="mb-4 overflow-x-auto">
                      <table className="w-full border border-gray-300 text-xs">
                        <thead><tr className="bg-gray-100">
                          <th className="border border-gray-300 px-2 py-1 text-left font-bold">{txt.subjects}</th>
                          {periods.map(p => <th key={p.id} className="border border-gray-300 px-2 py-1 text-center font-bold">{p.code || p.label_fr || `P${p.id}`}</th>)}
                          <th className="border border-gray-300 px-2 py-1 text-center font-bold">{txt.annual}</th>
                        </tr></thead>
                        <tbody>
                          {student.subjects.map(subj => (
                            <tr key={subj.subject_id}>
                              <td className="border border-gray-300 px-2 py-1 font-medium">{subj.subject_name}</td>
                              {periods.map(p => (
                                <td key={p.id} className="border border-gray-300 px-2 py-1 text-center">
                                  {subj.trimester_scores[`trim_${p.id}`] > 0 ? subj.trimester_scores[`trim_${p.id}`].toFixed(2) : '-'}
                                </td>
                              ))}
                              <td className="border border-gray-300 px-2 py-1 text-center font-bold">{subj.annual_average > 0 ? subj.annual_average.toFixed(2) : '-'}</td>
                            </tr>
                          ))}
                          <tr className="bg-gray-100 font-bold">
                            <td className="border border-gray-300 px-2 py-1" colSpan={periods.length + 1}>{txt.annualAvg}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center text-[#1e3a8a]">{student.general_average.toFixed(2)}</td>
                          </tr>
                          <tr className="bg-gray-100 font-bold">
                            <td className="border border-gray-300 px-2 py-1" colSpan={periods.length + 1}>{txt.annualRank}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center text-[#1e3a8a]">{student.annual_rank} / {student.total_students}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> {txt.workDecisions}</h4>
                      <div className="space-y-2">
                        {[
                          { key: "tableau_honneur", label: txt.honorRoll, color: "text-emerald-600" },
                          { key: "encouragements", label: txt.encouragements, color: "text-blue-600" },
                          { key: "felicitations", label: txt.felicitations, color: "text-cyan-600" },
                          { key: "avertissement_travail", label: txt.avertTravail, color: "text-orange-600" },
                          { key: "blame_travail", label: txt.blameTravail, color: "text-red-600" },
                        ].map(({ key, label, color }) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={(student as any)[key]}
                              onChange={e => updateStudentField(student.student_id, key as any, e.target.checked)}
                              className="w-4 h-4 text-[#1e3a8a] rounded" />
                            <span className={`text-sm ${color}`}>{label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{txt.obsProf}</label>
                        <textarea value={student.obs_prof_principal || ""}
                          onChange={e => updateStudentField(student.student_id, "obs_prof_principal", e.target.value)}
                          rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> {txt.conduct}</h4>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {[
                          { label: txt.absJustified, key: "absence_hours", type: "number" },
                          { label: txt.absUnjustified, key: "absence_unjustified", type: "number" },
                          { label: txt.exclusionDays, key: "exclusion_days", type: "number" },
                          { label: txt.exclusionHours, key: "exclusion_hours", type: "number" },
                          { label: txt.consigneDays, key: "consigne_days", type: "number" },
                          { label: txt.consigneHours, key: "consigne_hours", type: "number" },
                          { label: txt.conductNote, key: "conduct_note", type: "number", step: 0.5 },
                        ].map(({ label, key, type, step }) => (
                          <div key={key}>
                            <label className="block text-[10px] font-medium text-gray-500 uppercase">{label}</label>
                            <input type={type} step={step} value={(student as any)[key] ?? 0}
                              onChange={e => updateStudentField(student.student_id, key as any, type === "number" ? parseFloat(e.target.value) : e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={student.avertissement_conduite}
                            onChange={e => updateStudentField(student.student_id, "avertissement_conduite", e.target.checked)}
                            className="w-4 h-4 text-[#1e3a8a] rounded" />
                          <span className="text-sm text-orange-600">{txt.avertConduite}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={student.blame_conduite}
                            onChange={e => updateStudentField(student.student_id, "blame_conduite", e.target.checked)}
                            className="w-4 h-4 text-[#1e3a8a] rounded" />
                          <span className="text-sm text-red-600">{txt.blameConduite}</span>
                        </label>
                      </div>
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{txt.obsSurveillant}</label>
                        <textarea value={student.obs_surveillant || ""}
                          onChange={e => updateStudentField(student.student_id, "obs_surveillant", e.target.value)}
                          rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#1e3a8a]" /> {txt.finalDecision}</h4>
                      <div className="space-y-2 mb-3">
                        {[
                          { value: "Redouble", label: txt.repeat, color: "bg-amber-100 text-amber-700" },
                          { value: "Admis", label: txt.admitted, color: "bg-emerald-100 text-emerald-700" },
                          { value: "Retrograde", label: txt.retrograde, color: "bg-orange-100 text-orange-700" },
                          { value: "Exclu", label: txt.excluded, color: "bg-red-100 text-red-700" },
                          { value: "Aucune", label: txt.none, color: "bg-gray-100 text-gray-600" },
                        ].map(({ value, label, color }) => (
                          <label key={value} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name={`decision-${student.student_id}`}
                              checked={student.current_decision === value}
                              onChange={() => updateStudentField(student.student_id, "current_decision", value)}
                              className="w-4 h-4 text-[#1e3a8a]" />
                            <span className={`text-sm px-2 py-0.5 rounded-full ${color}`}>{label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{txt.obsChef}</label>
                        <textarea value={student.obs_chef_etablissement || ""}
                          onChange={e => updateStudentField(student.student_id, "obs_chef_etablissement", e.target.value)}
                          rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{txt.obsConseil}</label>
                        <textarea value={student.obs_conseil || ""}
                          onChange={e => updateStudentField(student.student_id, "obs_conseil", e.target.value)}
                          rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                      <button onClick={() => saveStudent(student)} disabled={saving}
                        className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center justify-center gap-2">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? txt.saving : txt.save}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}